import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { alertService } from '../../../../shared/services/alertService';
import { usePlanningWizard } from '../PlanningWizardContext';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface Props {
  mode: 'school' | 'vacancy';
}

/**
 * Shared Category / Management / Panel selection step. Used for both the
 * School step (activeIndex 2, Angular thirdStepSubmit) and the Vacancy step
 * (activeIndex 3, Angular fourthStepSubmit). Each of the three lists is a
 * table of checkboxes bound to the corresponding selected-id array.
 */
const StepCategoryPanel = ({ mode }: Props) => {
  const {
    userType1,
    isSCERT,
    detailsForm,
    categoryList,
    managementList,
    panelList,
    schType,
    setSchType,
    counsellingMinority,
    setCounsellingMinority,
    vacancyMinority,
    setVacancyMinority,
    selectedSchCategory,
    setSelectedSchCategory,
    selectedSchManage,
    setSelectedSchManage,
    selectedSchPanel,
    setSelectedSchPanel,
    vacUpdatedBy,
    setVacUpdatedBy,
    options,
    selectedVacCategory,
    setSelectedVacCategory,
    selectedVacManage,
    setSelectedVacManage,
    selectedVacPanel,
    setSelectedVacPanel,
    nextPage,
    previousPage,
    confirmationDetails,
  } = usePlanningWizard();

  const isSchool = mode === 'school';
  const directorate = String(detailsForm.values.Directorate);

  const selectedCategory = isSchool ? selectedSchCategory : selectedVacCategory;
  const setSelectedCategory = isSchool
    ? setSelectedSchCategory
    : setSelectedVacCategory;
  const selectedPanel = isSchool ? selectedSchPanel : selectedVacPanel;
  const setSelectedPanel = isSchool ? setSelectedSchPanel : setSelectedVacPanel;

  // Angular handleSchType: reset management selections per role, re-derive list.
  const handleSchType = (value: string) => {
    setSchType(value);
    if (userType1 === '29') {
      setSelectedSchManage(['2']);
      setSelectedVacManage(['2']);
    } else {
      setSelectedSchManage([]);
      setSelectedVacManage([]);
    }
  };

  const thirdStepSubmit = () => {
    if (schType === '1' || (schType === '2' && counsellingMinority)) {
      if (
        selectedSchCategory.length !== 0 &&
        selectedSchManage.length !== 0 &&
        selectedSchPanel.length !== 0
      ) {
        confirmationDetails();
        nextPage();
      } else {
        alertService.error(
          'Please select atleast one category, management and panel'
        );
      }
    } else if (schType === '2') {
      alertService.error('Please select minority type');
    } else {
      alertService.error('Please select school type');
    }
  };

  const fourthStepSubmit = () => {
    if (vacUpdatedBy || (schType === '2' && vacancyMinority)) {
      if (
        selectedVacCategory.length !== 0 &&
        selectedVacManage.length !== 0 &&
        selectedVacPanel.length !== 0
      ) {
        confirmationDetails();
        nextPage();
      } else {
        alertService.error(
          'Please select atleast one category, management and panel'
        );
      }
    } else {
      alertService.error(
        'Please select vacancy updated by, vacancies minority type'
      );
    }
  };

  const minorityRadios = (
    value: string,
    setValue: (v: string) => void,
    prefix: string
  ) => (
    <div className="field-radiobutton">
      <RadioButton
        inputId={`${prefix}1`}
        name={`${prefix}min`}
        value="1"
        checked={value === '1'}
        onChange={() => setValue('1')}
      />
      &nbsp;<span>Minority</span>&nbsp;&nbsp;
      <RadioButton
        inputId={`${prefix}2`}
        name={`${prefix}min`}
        value="2"
        checked={value === '2'}
        onChange={() => setValue('2')}
      />
      &nbsp;<span>Non-Minority</span>&nbsp;&nbsp;
      <RadioButton
        inputId={`${prefix}3`}
        name={`${prefix}min`}
        value="3"
        checked={value === '3'}
        onChange={() => setValue('3')}
      />
      &nbsp;<span>Opened</span>
    </div>
  );

  const checkboxCell = (
    row: any,
    arr: string[],
    setArr: React.Dispatch<React.SetStateAction<string[]>>,
    disabled = false
  ) => (
    <input
      type="checkbox"
      style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
      value={String(row.id)}
      disabled={disabled}
      checked={arr.includes(String(row.id))}
      onChange={(e) => {
        const id = String(row.id);
        setArr((prev) =>
          e.target.checked ? [...prev, id] : prev.filter((v) => v !== id)
        );
      }}
    />
  );

  // Table caption: the title plus a "select all" checkbox on the same line.
  // Checking it selects every row id in the list; unchecking clears them.
  const captionHeader = (
    list: any[],
    arr: string[],
    setArr: React.Dispatch<React.SetStateAction<string[]>>,
    disabled = false
  ) => {
    const ids = list.map((r) => String(r.id));
    const allChecked = ids.length > 0 && ids.every((id) => arr.includes(id));
    return (
      <div className="d-flex align-items-center gap-2">
        <input
          type="checkbox"
          style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
          disabled={disabled}
          checked={allChecked}
          onChange={(e) => setArr(e.target.checked ? ids : [])}
        />
      </div>
    );
  };

  return (
    <div className="card p-4">
      <div className="row">
        <div className="col">
          {isSchool ? (
            <b>
              {isSCERT
                ? 'Please select the type of institution from which candidate will participate in counselling.'
                : 'Please select the type of school from which candidate will participate in counselling.'}
            </b>
          ) : (
            <b>
              {isSCERT
                ? 'Please select the types of institution whose vacancies should be shown to the participate.'
                : 'Please select the types of school whose vacancies should be shown to the participate.'}
            </b>
          )}
        </div>
      </div>
      <br />

      {/* School: school-type + minority radios (hidden for directorate 4/5). */}
      {isSchool && directorate !== '5' && directorate !== '4' && (
        <div className="row">
          {!isSCERT && (
            <div className="col-4">
              <label>School Type</label>
              <div className="field-radiobutton">
                <RadioButton
                  inputId="sch1"
                  name="sch"
                  value="1"
                  checked={schType === '1'}
                  onChange={() => handleSchType('1')}
                />
                &nbsp;<span>Government</span>&nbsp;&nbsp;
                <RadioButton
                  inputId="sch2"
                  name="sch"
                  value="2"
                  checked={schType === '2'}
                  onChange={() => handleSchType('2')}
                />
                &nbsp;<span>Aided</span>
              </div>
            </div>
          )}
          {schType === '2' && (
            <div className="col">
              <label>Minority Type</label>
              {minorityRadios(
                counsellingMinority,
                setCounsellingMinority,
                'cmnrt'
              )}
            </div>
          )}
        </div>
      )}

      {/* Vacancy: vacancy-updated-by + vacancy minority. */}
      {!isSchool && (
        <div className="row">
          <div className="col-4">
            <label>Vacancy Updated By</label>
            <Dropdown
              value={vacUpdatedBy}
              options={options.vacancyLevels}
              optionLabel="label"
              optionValue="value"
              placeholder="Select.."
              showClear={!!vacUpdatedBy}
              appendTo="self"
              className="w-100"
              onChange={(e) => setVacUpdatedBy(e.value ?? '')}
            />
          </div>
          {schType === '2' && (
            <div className="col">
              <label>Vacancies Minority Type</label>
              {minorityRadios(vacancyMinority, setVacancyMinority, 'vmnrt')}
            </div>
          )}
        </div>
      )}

      <div className="row mt-4">
        <div className="col">
          <DataTable
            key={`cat-${selectedCategory.join(',')}`}
            value={categoryList}
            showGridlines
            responsiveLayout="scroll"
          >
            <Column
              body={(row) =>
                checkboxCell(row, selectedCategory, setSelectedCategory)
              }
              style={{ width: '3rem' }}
              header={captionHeader(
                categoryList,
                selectedCategory,
                setSelectedCategory
              )}
            />
            <Column field="label" header={'Category'} />
          </DataTable>
        </div>
        <div className="col">
          <DataTable
            key={`mng-${(isSchool ? selectedSchManage : selectedVacManage).join(',')}`}
            value={managementList}
            showGridlines
            responsiveLayout="scroll"
          >
            <Column
              body={(row) =>
                isSchool
                  ? checkboxCell(row, selectedSchManage, setSelectedSchManage)
                  : checkboxCell(
                      row,
                      selectedVacManage,
                      setSelectedVacManage,
                      userType1 === '29'
                    )
              }
              style={{ width: '3rem' }}
              header={
                isSchool
                  ? captionHeader(
                      managementList,
                      selectedSchManage,
                      setSelectedSchManage
                    )
                  : captionHeader(
                      managementList,
                      selectedVacManage,
                      setSelectedVacManage,
                      userType1 === '29'
                    )
              }
            />
            <Column field="label" header={'Management'} />
          </DataTable>
        </div>
        <div className="col">
          <DataTable
            key={`pnl-${selectedPanel.join(',')}`}
            value={panelList}
            showGridlines
            responsiveLayout="scroll"
          >
            <Column
              header={captionHeader(panelList, selectedPanel, setSelectedPanel)}
              body={(row) => checkboxCell(row, selectedPanel, setSelectedPanel)}
              style={{ width: '3rem' }}
            />
            <Column field="label" header={'Panel'} />
          </DataTable>
        </div>
      </div>
      <br />

      <div className="row">
        <div className="col-6">
          <Button
            type="button"
            icon="pi pi-angle-left"
            label="Previous"
            className="p-button-danger"
            onClick={previousPage}
          />
        </div>
        <div className="col-6 text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Next"
            className="p-button-success"
            onClick={isSchool ? thirdStepSubmit : fourthStepSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StepCategoryPanel;
